import {
  captureObservabilityError,
  useAnalytics,
  useAuthentication,
  useQueryParam,
  withRegisterSubmit,
} from '@psyomics/ui-common';
import { Checkboxes, Information, PageLayout, RegisterForm } from '@psyomics/ui-components';
import { type ComponentProps, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { getConfig } from '../config';
import { ReferralInvite } from '../features/referral/generated/referral';
import { QueryError, useReferralInviteQuery } from '../features/referral/hooks/use-referral-invite-query';
import { useLandingContext } from '../shared/contexts/landing-context';
import { usePatientAppBarContext } from '../shared/contexts/patient-app-bar';

import { CRISIS_ACKNOWLEDGED_KEY } from './crisis-acknowledgement';
import { PATIENT_REDIRECT_WHITELIST } from './redirect-whitelists';

const useObservabilityAndAnalytics = ({
  analytics,
  authSystemId,
  error,
  invite,
  invitePublicId,
  isAuthenticated,
}: {
  analytics: ReturnType<typeof useAnalytics>;
  authSystemId?: string;
  error?: QueryError;
  invite?: ReferralInvite;
  invitePublicId?: string;
  isAuthenticated: boolean;
}) => {
  useEffect(() => {
    if (error)
      captureObservabilityError(
        `Referral invite query failed for invite public id ${invitePublicId}: ${error.message}`
      );
  }, [error, invitePublicId]);

  useEffect(() => {
    if (!isAuthenticated || !authSystemId) return;

    const serviceCode = invite?.referralSession?.referral?.service?.code;
    const timestamp = new Date().toISOString();

    analytics.identify({
      distinctId: authSystemId,
      propertiesToSetOnce: {
        signup_date: timestamp,
        ...(invitePublicId && { current_invite: invitePublicId }),
        ...(serviceCode && { current_service: serviceCode }),
      },
    });
  }, [isAuthenticated, authSystemId, invitePublicId, invite, analytics]);
};

const RegisterWithSubmit = withRegisterSubmit(RegisterForm, {
  localStorageData: { [CRISIS_ACKNOWLEDGED_KEY]: 'true' },
  redirectWhitelist: PATIENT_REDIRECT_WHITELIST,
});

const useRenderCheckboxes = (setShowUrgentHelp: (show: boolean) => void) =>
  useCallback(
    ({
      control,
      errors,
    }: {
      readonly control: ComponentProps<typeof Checkboxes>['control'];
      readonly errors: ComponentProps<typeof Checkboxes>['errors'];
    }) => <Checkboxes control={control} errors={errors} onUrgentHelpClick={() => setShowUrgentHelp(true)} />,
    [setShowUrgentHelp]
  );

const SelfHostedRegister = () => {
  const { patientApiUrl } = getConfig();
  const { setShowUrgentHelp } = usePatientAppBarContext();
  const invitePublicId = useQueryParam('invitePublicId') ?? undefined;
  const redirect = useQueryParam('redirect') ?? undefined;
  const { preSignUpText } = useLandingContext();
  const { authSystemId, isAuthenticated } = useAuthentication();
  const analytics = useAnalytics();
  const [invite, , error] = useReferralInviteQuery(invitePublicId);

  const getAuthSessionContext = useCallback(
    () => ({
      invitePublicId,
    }),
    [invitePublicId]
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useObservabilityAndAnalytics({
    analytics,
    authSystemId,
    error,
    invite,
    invitePublicId,
    isAuthenticated,
  });

  const signInUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (invitePublicId != null) {
      params.set('invitePublicId', invitePublicId);
    }
    if (redirect != null) {
      params.set('redirect', redirect);
    }

    const query = params.toString();
    const queryString = query ? `?${query}` : '';
    return `/login${queryString}`;
  }, [invitePublicId, redirect]);

  const renderCheckboxes = useRenderCheckboxes(setShowUrgentHelp);

  return (
    <PageLayout width="narrow">
      <RegisterWithSubmit
        apiUrl={patientApiUrl}
        getAuthSessionContext={getAuthSessionContext}
        headerKey="auth.register.mainHeader"
        preSignUpText={preSignUpText}
        renderCheckboxes={renderCheckboxes}
        renderInformation={Information}
        showRegistrationMethodToggle={true}
        signInUrl={signInUrl}
        submitButtonKey="auth.register.startSurveyButtonText"
        switchToEmailLabelKey="auth.register.switchToEmailUsernameLabel"
        switchToMobileLabelKey="auth.register.switchToMobileUsernameLabel"
      />
    </PageLayout>
  );
};

export { SelfHostedRegister };
